import './DevPanel_AmplienceUsagePanel.scss';

import React, { useEffect, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography } from '@mui/material';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import {
  buildAmplienceLink,
  skipCommonModules
} from '../../utils/amplience/amplience';
import DevPanel_AmplienceUsagePagination from './DevPanel_AmplienceUsagePagination';

let globalUsageCache = null;
let globalUsagePromise = null;

export const clearUsageCache = () => {
  globalUsageCache = null;
  globalUsagePromise = null;
};

export const fetchAllUsageData = ( utilityServerDomain ) => {
  if( globalUsageCache ){
    return Promise.resolve( globalUsageCache );
  }
  if( !globalUsagePromise ){
    globalUsagePromise = fetch( `${utilityServerDomain}/devpanel?action=amplience-usage` )
      .then( ( res ) => {
        if( !res.ok ){
          throw new Error( `HTTP error! Status: ${res.status}` );
        }
        return res.json();
      } )
      .then( ( data ) => {
        globalUsageCache = data.usage || {};
        return globalUsageCache;
      } )
      .catch( ( err ) => {
        globalUsagePromise = null;
        throw err;
      } );
  }
  return globalUsagePromise;
};

const NestedUsageAccordion = ( { type, pages } ) => {
  const [page, setPage] = useState( 1 );
  const [searchTerm, setSearchTerm] = useState( '' );
  const itemsPerPage = 5;

  const filteredPages = useMemo( () => {
    const search = searchTerm.toLowerCase();
    return pages.filter( ( pageItem ) => (
      pageItem.name.toLowerCase().includes( search ) ||
      pageItem.id.toLowerCase().includes( search ) ||
      pageItem.uri.toLowerCase().includes( search )
    ) );
  }, [pages, searchTerm] );

  const count = Math.ceil( filteredPages.length / itemsPerPage );
  const displayedPages = filteredPages.slice( ( page - 1 ) * itemsPerPage, page * itemsPerPage );

  useEffect( () => {
    setPage( 1 );
  }, [filteredPages] );

  return (
    <Accordion className='nested-accordion'>
      <AccordionSummary expandIcon={ <ExpandMoreIcon fontSize='small' /> }>
        <Typography variant='body2' className='nested-title'>
          { type } ({ pages.length })
        </Typography>
      </AccordionSummary>
      <AccordionDetails className='nested-details'>
        <Box className='search-container'>
          <TextField
            fullWidth
            size='small'
            placeholder='Search in this category...'
            value={ searchTerm }
            onChange={ ( e ) => setSearchTerm( e.target.value ) }
            variant='outlined'
          />
        </Box>
        { filteredPages.length > 0 ? (
          <>
            <List dense disablePadding>
              { displayedPages.map( ( pageItem ) => (
                <ListItem
                  key={ pageItem.id }
                  disableGutters
                  className='usage-list-item'
                >
                  <ListItemText
                    primary={ <Typography variant='body2' className='page-name'>{ pageItem.name }</Typography> }
                    secondary={
                      <Box className='secondary-info'>
                        <Typography variant='body2' className='url-label'>
                          URL:{ ' ' }
                          <Link
                            href={ `${window.location.origin}/${pageItem.uri?.replace( /^\//, '' )}?User-Agent=Gomez` }
                            target='_blank'
                            rel='noreferrer'
                            underline='hover'
                            color='primary'
                            variant='body2'
                            className='url-link'
                          >
                            { `${window.location.origin}/${pageItem.uri?.replace( /^\//, '' )}` }
                          </Link>
                        </Typography>
                        <Typography variant='body2' className='id-label'>
                          Amplience Link:{ ' ' }
                          <Link
                            href={ buildAmplienceLink( pageItem.id, pageItem ) }
                            target='_blank'
                            rel='noreferrer'
                            underline='hover'
                            color='primary'
                            variant='body2'
                            className='id-link'
                          >
                            { pageItem.id }
                          </Link>
                        </Typography>
                      </Box>
                    }
                    disableTypography
                  />
                </ListItem>
              ) ) }
            </List>
            <DevPanel_AmplienceUsagePagination
              count={ count }
              page={ page }
              onChange={ ( _, value ) => setPage( value ) }
            />
          </>
        ) : (
          <Typography variant='body2' className='no-usage-text'>No pages match your search.</Typography>
        ) }
      </AccordionDetails>
    </Accordion>
  );
};

const DevPanel_AmplienceUsagePanel = ( props ) => {
  const { module } = props;
  const { id, moduleName, type } = module || {};

  const [editions, setEditions] = useState( [] );
  const [loadingEditions, setLoadingEditions] = useState( false );
  const [editionsError, setEditionsError] = useState( null );
  const { serverRouteUtility } = useAppConfigContext();

  const [usageData, setUsageData] = useState( {
    pages: [],
    loading: false,
    error: null
  } );

  const isSkipModule = useMemo( () => (
    skipCommonModules.includes( type ) || skipCommonModules.includes( moduleName )
  ), [type, moduleName] );

  // Fetch editions for the content item
  useEffect( () => {
    let cancelled = false;
    const fetchEditions = async() => {
      if( !id || isSkipModule ){
        return;
      }
      setLoadingEditions( true );
      setEditionsError( null );
      try {
        const response = await fetch( `${serverRouteUtility}/devpanel?action=amplience-editions&id=${id}` );
        if( !response.ok ){
          throw new Error( `HTTP error! Status: ${response.status}` );
        }
        const editionData = await response.json();
        if( !cancelled ){
          setEditions( editionData );
        }
      }
      catch ( error ){
        // eslint-disable-next-line no-console
        console.error( 'Failed to get editions:', error );
        if( !cancelled ){
          setEditionsError( 'Failed to load editions.' );
        }
      }
      finally {
        if( !cancelled ){
          setLoadingEditions( false );
        }
      }
    };

    fetchEditions();
    return () => {
      cancelled = true;
    };
  }, [id, isSkipModule, serverRouteUtility] );

  // Fetch usage across pages using the backend API
  useEffect( () => {
    let cancelled = false;
    const fetchUsage = async() => {
      if( !moduleName || isSkipModule ){
        return;
      }

      setUsageData( ( prev ) => ( { ...prev, loading: true, error: null } ) );

      try {
        const usageCache = await fetchAllUsageData( serverRouteUtility );

        if( !cancelled ){
          setUsageData( {
            pages: usageCache[moduleName] || [],
            loading: false,
            error: null
          } );
        }
      }
      catch ( error ){
        // eslint-disable-next-line no-console
        console.error( 'Error fetching component usage:', error );
        if( !cancelled ){
          setUsageData( ( prev ) => ( {
            ...prev,
            loading: false,
            error: `Failed to load component usage references: ${error.message}`
          } ) );
        }
      }
    };

    fetchUsage();
    return () => {
      cancelled = true;
    };
  }, [moduleName, isSkipModule, serverRouteUtility] );

  // Group pages by pageType
  const groupedPages = useMemo( () => usageData.pages.reduce( ( acc, page ) => {
    const type = page.pageType || 'Other';
    if( !acc[type] ){
      // eslint-disable-next-line no-param-reassign
      acc[type] = [];
    }
    acc[type].push( page );
    return acc;
  }, {} ), [usageData.pages] );

  return (
    <div className='DevPanel_AmplienceUsagePanel'>
      <Accordion
        defaultExpanded
        className='usage-accordion'
      >
        <AccordionSummary
          expandIcon={ <ExpandMoreIcon /> }
          aria-controls='usage-panel-content'
          id='usage-panel-header'
        >
          <Typography component='span' variant='subtitle1' className='accordion-title'>
            Where are &quot;{ moduleName }&quot; components used?
          </Typography>
        </AccordionSummary>
        <AccordionDetails className='accordion-details'>
          { usageData.loading ? (
            <Box className='loading-box'>
              <CircularProgress size={ 20 } />
              <Typography variant='body2' className='loading-text'>Scanning pages for usage...</Typography>
            </Box>
          ) : usageData.error ? (
            <Alert severity='error'>{ usageData.error }</Alert>
          ) : Object.keys( groupedPages ).length > 0 ? (
            Object.entries( groupedPages ).map( ( [type, pages] ) => (
              <NestedUsageAccordion
                key={ type }
                type={ type }
                pages={ pages }
              />
            ) )
          ) : (
            <Typography variant='body2' className='no-usage-text'>This component type is not used in any published pages.</Typography>
          ) }
        </AccordionDetails>
      </Accordion>

      <Accordion className='editions-accordion'>
        <AccordionSummary
          expandIcon={ <ExpandMoreIcon /> }
          aria-controls='editions-panel-content'
          id='editions-panel-header'
        >
          <Typography component='span' variant='subtitle1' className='accordion-title'>
            Editions / Versions
          </Typography>
        </AccordionSummary>
        <AccordionDetails className='accordion-details'>
          { loadingEditions ? (
            <Box className='loading-box'>
              <CircularProgress size={ 20 } />
              <Typography variant='body2' className='loading-text'>Loading editions...</Typography>
            </Box>
          ) : editionsError ? (
            <Alert severity='error'>{ editionsError }</Alert>
          ) : editions.length > 0 ? (
            <Box className='editions-container'>
              { editions.map( ( edition ) => (
                <Box key={ edition.id } className='edition-item'>
                  <Typography variant='body2' className='edition-title'>
                    { edition.label || `Version ${edition.version}` }
                  </Typography>
                  <Typography variant='caption' className='edition-subtitle'>
                    Version: { edition.version } | ID: { edition.id }
                  </Typography>
                </Box>
              ) ) }
            </Box>
          ) : (
            <Typography variant='body2' className='no-usage-text'>No editions found for this content item.</Typography>
          ) }
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default DevPanel_AmplienceUsagePanel;
